import { Select } from "antd";
import moment from "moment";
import XLSX from 'sheetjs-style';
import * as fileSaver from 'file-saver';
import jsPDF from 'jspdf';

function uniqueValues(array = [], codeKey, nameKey, type) {
    var uniqueCodeKey = {};

    var data = array?.map((eachProperty) => {
        if (eachProperty && eachProperty[codeKey] && !uniqueCodeKey[eachProperty[codeKey]]) {
            uniqueCodeKey[eachProperty[codeKey]] = true;
            return (type === "concatenate" ?
                <Select.Option value={eachProperty[codeKey]} key={eachProperty[codeKey]}>
                    {`${eachProperty[codeKey]} - ${eachProperty[nameKey]}`}
                </Select.Option>
                : <Select.Option value={eachProperty[codeKey]} key={eachProperty[codeKey]}>
                    {eachProperty[nameKey]}
                </Select.Option>);
        };
    });
    return data?.filter((result) => result !== undefined);
};

function parseDate(date) {
    if (!date) return null;

    let parsedDate = moment(date, moment.ISO_8601, true);

    if (!parsedDate.isValid()) {
        const formats = ['MM-DD-YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'];
        for (const format of formats) {
            parsedDate = moment(date, format, true);
            if (parsedDate.isValid()) {
                break;
            };
        };
    };

    return parsedDate.isValid() ? parsedDate : null;
};

function formatDate(date) {
    const parsedDate = parseDate(date);

    if (!parsedDate) {
        return '-';
    };

    return parsedDate.format('MM/DD/YYYY');
};

function formatCurrency(amount) {
    const numberAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (isNaN(numberAmount) || numberAmount == null || numberAmount === '') {
        return "-";
    };

    return numberAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD'
    });
};

function handleFormatText(text) {
    if (typeof text === 'string') {
        return text.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    }
    else if (typeof text === 'boolean') {
        return text ? "True" : "False";
    }
    else {
        return text;
    };
};

function formatAddress(address) {
    if (!address) {
        return "";
    }

    const formattedAddress = [
        address.address,
        address.street,
        address.block,
        address.city,
        address.state ? `${address.state} ${address.zipCode}` : "",
        address.country
    ]
        .filter(part => part)
        .join(", ");

    return formattedAddress;
}

const validateDateFormat = (value) => {
    const isoDatePattern = /^\d{4}-\d{2}-\d{2}?$/;
    if (!isoDatePattern.test(value)) {
        return false;
    };
    const date = new Date(value);
    return !isNaN(date.getTime());
};

const getDataType = (value, key) => {
    if ((typeof value === 'string' && !isNaN(Date.parse(value)) && validateDateFormat(value))) {
        return 'date';
    }
    else if (!isNaN(value) && typeof value === 'number') {
        return 'number';
    }
    else {
        return typeof value;
    };
};

function formatTime(time) {
    if (!time) return "12:00 AM";

    let hours = Math.floor(time / 100);
    let minutes = time % 100;

    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")} ${period}`;
}
const deepEqual = (value1, value2) => {
    if (value1 === value2) return true;
    if (typeof value1 !== 'object' || value1 === null || typeof value2 !== 'object' || value2 === null) {
        return false;
    };
    if (Array.isArray(value1) && Array.isArray(value2)) {
        return areArraysEqual(value1, value2);
    };
    if (!Array.isArray(value1) && !Array.isArray(value2)) {
        return areObjectsEqual(value1, value2);
    };

    return false;
};

const handleNumericInput = (e, field) => {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];

    if (allowedKeys.includes(e.key)) return;

    if (field === "decimal") {
        if (!e.key.match(/^[0-9.]$/) || (e.key === "." && e.target.value.includes("."))) {
            e.preventDefault();
        };
    }
    else {
        if (!e.key.match(/^[0-9]$/)) {
            e.preventDefault();
        };
    };
};

const showToastInfo = (value, type) => {
    let iconColor = {
        info: "#1ea5ba",
        success: "#4f8a11",
        deleted: "#f5222d"
    };

    return notification.info({
        description: value,
        placement: 'topRight', duration: 3,
        icon: <MdInfo style={{ color: iconColor[type] }} size={24} />
    });
};

const getFormattedDate = (date) => {
    const now = moment();
    const commentDate = moment(date);

    if (now.diff(commentDate, 'hours') < 24) {
        return moment(commentDate).fromNow();
    }

    if (now.diff(commentDate, 'days') < 7) {
        return moment(commentDate).format("dddd h:mm A");
    }

    if (now.year() === commentDate.year()) {
        return moment(commentDate).format("MMM DD hh:mm A");
    }

    return moment(commentDate).format("MMM DD, YYYY hh:mm A");
};

function formattedPdfExportData(data, width, height, rowsPerPage, fileName) {
    const columnLabels = Object.keys(data[0]).map(column => handleFormatText(column));

    const pdf = new jsPDF({
        orientation: 'landscape'
    });
    const fileExtension = '.pdf';
    const contentWidth = columnLabels.length * width;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const x = (pageWidth - contentWidth) / 2;
    const cellWidth = width;
    const minCellHeight = height;
    const headerCellHeight = 30;
    const title = fileName;
    const titleX = (pageWidth - contentWidth) / 2;

    pdf.setFontSize(15);
    pdf.text(title, titleX, 10);
    let y = 20;
    const chunks = [];

    for (let i = 0; i < data.length; i += rowsPerPage) {
        chunks.push(data.slice(i, i + rowsPerPage));
    };

    pdf.setFillColor(220);
    chunks.forEach((chunk, chunkIndex) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        columnLabels.forEach((label, index) => {
            pdf.setFillColor(220);
            pdf.rect(x + index * cellWidth, y, cellWidth, headerCellHeight, 'F');
            pdf.rect(x + index * cellWidth, y, cellWidth, headerCellHeight);
            const lines = pdf.splitTextToSize(label, cellWidth - 2);
            const cellHeight = Math.max(lines.length * 4, headerCellHeight);
            const lineHeight = lines.length * 4;
            const remainingHeight = cellHeight - lineHeight;
            const textY = y + remainingHeight / 2 + (headerCellHeight - lineHeight) / 2;

            lines.forEach((line, lineIndex) => {
                pdf.text(line, x + index * cellWidth + cellWidth / 2, textY + lineIndex * 4, { align: 'center', valign: 'middle' });
            });
        });

        y += headerCellHeight;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        chunk.forEach((item, rowIndex) => {
            Object.keys(item).forEach((key, colIndex) => {
                const value = item[key] === null || item[key] === undefined ? '' : item[key];
                const lines = pdf.splitTextToSize(String(value), cellWidth - 2);
                const cellHeight = Math.max(lines.length * 4, minCellHeight);
                const lineHeight = lines.length * 4;
                const remainingHeight = cellHeight - lineHeight;
                const textY = y + rowIndex * cellHeight + remainingHeight / 2 + (cellHeight - lineHeight) / 2;

                pdf.rect(x + colIndex * cellWidth, y + rowIndex * cellHeight, cellWidth, cellHeight);
                lines.forEach((line, lineIndex) => {
                    pdf.text(line, x + colIndex * cellWidth + cellWidth / 2, textY + lineIndex * 4, { align: 'center', valign: 'middle' });
                });
            });
        });

        if (chunkIndex < chunks.length - 1) {
            pdf.addPage();
            y = 10;
        }
        else {
            y += chunks[chunkIndex].length * minCellHeight;
        };
    });

    const downloadDateTime = new Date();
    const downloadDate = downloadDateTime.toLocaleDateString();
    const downloadTime = downloadDateTime.toLocaleTimeString();

    pdf.setFontSize(10);
    pdf.text(`Date: ${downloadDate}`, pageWidth - 50, pdf.internal.pageSize.getHeight() - 15);
    pdf.text(`Time: ${downloadTime}`, pageWidth - 50, pdf.internal.pageSize.getHeight() - 10);
    pdf.save(`${fileName}_${moment(new Date()).format("YYYY-MM-DD_hh-mm A")}${fileExtension}`);
};


function exportToExcel(json, fileName) {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const workSheet = XLSX.utils.json_to_sheet(json);
    const columnWidths = [];

    for (let i in workSheet) {
        if (typeof (workSheet[i]) != "object") continue;
        let cell = XLSX.utils.decode_cell(i);
        if (cell.r === 0) {
            workSheet[i].s = {
                font: {
                    name: 'Calibri',
                    bold: true,
                    color: { rgb: "#A9A9A9" }
                },
                alignment: {
                    vertical: "center",
                    horizontal: "center",
                },
                border: {
                    bottom: {
                        style: "thin",
                        color: "000000"
                    }
                },
            };
        }
        else {
            workSheet[i].s = {
                alignment: {
                    wrapText: true,
                },
            };
        };
        if (!workSheet['!rows']) {
            workSheet['!rows'] = [];
        };
        if (!workSheet['!rows'][cell.r]) {
            workSheet['!rows'][cell.r] = {};
        };
        workSheet['!rows'][cell.r].hpx = 20;
        const columnWidth = {
            wch: Math.max(
                columnWidths[cell.c] || 0,
                String(workSheet[i].v || '').length
            )
        };
        columnWidths[cell.c] = columnWidth.wch;
    };

    workSheet['!cols'] = columnWidths.map(width => ({ wch: width }));
    const workBook = { Sheets: { 'data': workSheet }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(workBook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: fileType });
    fileSaver.saveAs(data, `${fileName}_${moment(new Date()).format("yyyy-MM-DD_hh-mm A")}${fileExtension}`);
};


function createPDF(data, width, height, rowsPerPage, fileName) {
    // Get the column labels from the keys of the first data object
    const columnLabels = Object.keys(data[0]).map(column => handleFormatText(column));

    // Create a new jsPDF instance
    const pdf = new jsPDF({
        orientation: 'landscape'
    });
    const fileExtension = '.pdf';

    // Calculate the content width
    const contentWidth = columnLabels.length * width;

    // Calculate the horizontal position to center the content
    const pageWidth = pdf.internal.pageSize.getWidth();
    const x = (pageWidth - contentWidth) / 2;

    // Set cell width and minimum cell height
    const cellWidth = width;
    const minCellHeight = height;

    // Set header column cell height
    const headerCellHeight = 30;

    // Add title
    const title = fileName;
    const titleX = (pageWidth - contentWidth) / 2; // Calculate x position to center title
    pdf.setFontSize(15);
    pdf.text(title, titleX, 10);
    let y = 20; // Adjusted to add space below the title

    // Split data into chunks of rowsPerPage
    const chunks = [];
    for (let i = 0; i < data.length; i += rowsPerPage) {
        chunks.push(data.slice(i, i + rowsPerPage));
    }

    // Set gray background color for header columns
    pdf.setFillColor(220); // Use a gray value between 0 and 255

    // Iterate over data chunks
    chunks.forEach((chunk, chunkIndex) => {
        // Add column headers to the PDF with borders
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        columnLabels.forEach((label, index) => {
            pdf.setFillColor(220); // Gray background color
            pdf.rect(x + index * cellWidth, y, cellWidth, headerCellHeight, 'F'); // Draw filled rectangle for background
            pdf.rect(x + index * cellWidth, y, cellWidth, headerCellHeight); // Draw cell border

            // Split label text to fit within cell width
            const lines = pdf.splitTextToSize(label, cellWidth - 2); // Subtracting padding
            const cellHeight = Math.max(lines.length * 4, headerCellHeight); // Adjust cell height based on the number of lines
            const lineHeight = lines.length * 4; // Calculate total height of the wrapped text
            const remainingHeight = cellHeight - lineHeight; // Calculate remaining height after text

            // Calculate y position to vertically center text
            const textY = y + remainingHeight / 2 + (headerCellHeight - lineHeight) / 2;

            // Add each line of the label text
            lines.forEach((line, lineIndex) => {
                pdf.text(line, x + index * cellWidth + cellWidth / 2, textY + lineIndex * 4, { align: 'center', valign: 'middle' }); // Adjust position for text
            });
        });

        // Reset y position for the data rows
        y += headerCellHeight;

        // Add data rows to the PDF with borders
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        chunk.forEach((item, rowIndex) => {
            Object.keys(item).forEach((key, colIndex) => {
                const value = item[key] === null || item[key] === undefined ? '' : item[key];
                const lines = pdf.splitTextToSize(String(value), cellWidth - 2); // Subtracting padding
                const cellHeight = Math.max(lines.length * 4, minCellHeight); // Adjust cell height based on the number of lines
                const lineHeight = lines.length * 4; // Calculate total height of the wrapped text
                const remainingHeight = cellHeight - lineHeight; // Calculate remaining height after text
                const textY = y + rowIndex * cellHeight + remainingHeight / 2 + (cellHeight - lineHeight) / 2;

                pdf.rect(x + colIndex * cellWidth, y + rowIndex * cellHeight, cellWidth, cellHeight); // Draw cell border
                lines.forEach((line, lineIndex) => {
                    pdf.text(line, x + colIndex * cellWidth + cellWidth / 2, textY + lineIndex * 4, { align: 'center', valign: 'middle' }); // Adjust position for text
                });
            });
        });

        // Add a new page if there are more chunks
        if (chunkIndex < chunks.length - 1) {
            pdf.addPage();
            y = 10; // Reset y position for the new page
        } else {
            y += chunks[chunkIndex].length * minCellHeight; // Adjust y position for the remaining rows on the last page
        }
    });

    // Add date and time of the file downloaded
    const downloadDateTime = new Date();
    const downloadDate = downloadDateTime.toLocaleDateString();
    const downloadTime = downloadDateTime.toLocaleTimeString();
    pdf.setFontSize(10);
    pdf.text(`Date: ${downloadDate}`, pageWidth - 50, pdf.internal.pageSize.getHeight() - 15);
    pdf.text(`Time: ${downloadTime}`, pageWidth - 50, pdf.internal.pageSize.getHeight() - 10);

    // Save the PDF with a filename
    pdf.save(`${fileName}_${moment(new Date()).format("YYYY-MM-DD_hh-mm A")}${fileExtension}`);
}

export { uniqueValues, parseDate, formatDate, formatCurrency, showToastInfo, getFormattedDate, formattedPdfExportData, handleFormatText, handleNumericInput, formatAddress, formatTime, deepEqual, getDataType, exportToExcel, createPDF };